import { useMemo } from "react";

import type { StreamResource } from "@prequel/import-react";
import { type ListStreamsOptions, useListStreams } from "@prequel/import-react";
import { Status, StatusLight, type TableRow } from "@prequel/shared-components";

import { useAllDatasets } from "@/api/import/datasets";
import { useAllProviders } from "@/api/import/providers";
import ResourceTable from "@/components/ResourceTable";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";

const COLUMNS = ["Name", "Provider", "Dataset", "Status"];

// Reads the destination from the route rather than a prop so it satisfies ResourceTable's
// useQuery contract, which only forwards pagination params.
const useDestinationStreams = (options?: ListStreamsOptions) => {
  const destinationID = useRequiredParam("destinationID");

  return useListStreams({ ...options, destinationId: destinationID });
};

const DestinationStreams = () => {
  const { items: datasets } = useAllDatasets();
  const { items: providers } = useAllProviders();

  const datasetsByID = useMemo(() => new Map(datasets.map((dataset) => [dataset.id, dataset])), [datasets]);
  const providersByID = useMemo(() => new Map(providers.map((provider) => [provider.id, provider])), [providers]);

  const rowMapper = (stream: StreamResource): TableRow => {
    const dataset = datasetsByID.get(stream.dataset_id);
    const provider = dataset ? providersByID.get(dataset.provider_id) : undefined;

    return {
      id: stream.id,
      to: `/import/streams/${stream.id}`,
      cells: [
        { value: stream.name },
        { value: provider?.name ?? "-" },
        { value: dataset?.name ?? "-" },
        {
          value: (
            <span className="flex items-center gap-2">
              <StatusLight status={stream.is_enabled ? Status.ACTIVE : Status.INACTIVE} />
              {stream.is_enabled ? "Enabled" : "Disabled"}
            </span>
          ),
        },
      ],
    };
  };

  return (
    <ResourceTable<StreamResource>
      columns={COLUMNS}
      useQuery={useDestinationStreams}
      rowMapper={rowMapper}
      emptyMessage="No streams configured for this destination."
    />
  );
};

export default DestinationStreams;
